// Day arithmetic for Growth › Schedule. A day is "YYYY-MM-DD", a calendar date
// with no zone: the server says which zone each kind of email sits in
// (services/growth/schedule.ts), so nothing here converts one.

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/

/** A real "YYYY-MM-DD", or null. */
export function validDay(v: string | null | undefined): string | null {
  const m = v ? DAY_RE.exec(v) : null
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return d.toISOString().slice(0, 10) === v ? v : null
}

function utc(day: string): Date {
  return new Date(`${day}T00:00:00Z`)
}

export function addDays(day: string, n: number): string {
  const d = utc(day)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** The same day of the month `n` months away, or that month's last day. */
export function addMonths(day: string, n: number): string {
  const d = utc(day)
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1))
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  target.setUTCDate(Math.min(d.getUTCDate(), last))
  return target.toISOString().slice(0, 10)
}

/** Monday of the week `day` is in. */
export function weekStart(day: string): string {
  return addDays(day, -((utc(day).getUTCDay() + 6) % 7))
}

/** The month of `day` as whole weeks, Monday first: what the server's default range is too. */
export function monthGrid(day: string): { from: string; to: string } {
  const first = `${day.slice(0, 8)}01`
  const last = addDays(addMonths(first, 1), -1)
  return { from: weekStart(first), to: addDays(weekStart(last), 6) }
}

/** Every day from `from` to `to`. */
export function daysFrom(from: string, to: string): string[] {
  const out: string[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d)
  return out
}

export function sameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7)
}

/** "Tuesday, November 4" (the year when it isn't this one). */
export function dayLabel(day: string, opts: { weekday?: 'long' | 'short'; year?: boolean } = {}): string {
  return utc(day).toLocaleDateString(undefined, {
    weekday: opts.weekday ?? 'long',
    day: 'numeric',
    month: 'long',
    ...(opts.year ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  })
}

/** "November 2026" */
export function monthLabel(day: string): string {
  return utc(day).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "2:30 PM" in a zone. */
export function timeIn(iso: string, timeZone: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone })
  } catch {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  }
}
